import { useState } from "react";
import { message } from "antd";
import dayjs from "dayjs";
import cloneDeep from "lodash.clonedeep";
import { useTranslation } from "react-i18next";
import { v4 as uuid } from "uuid";

import { programAutomationActions } from "@fitness-app/app-store";
import {
  TemplateType,
  type ScheduledPopUpMessagesAutomationFromAutomation,
} from "@fitness-app/data-models/entities/AutomationTemplate";
import { CampaignType, MediaContentType } from "@fitness-app/data-models/entities/Campaign";
import {
  ProgramAutomationScheduleAnchor,
  type ProgramAutomation,
  type ProgramAutomationScheduledTask,
  type WeekdayNumberToString,
} from "@fitness-app/data-models/entities/ProgramAutomation";

import { useFormHandler } from "~/hooks/useFormHandler";
import { type CampaignMessageComposeFormModel } from "~/modules/Campaigns/CampaignForm/types";
import { useAppDispatch, useAppSelector } from "~/store/initializeStore";
import { transformFromFiltersToFormData, transformFromFormDataToFilters } from "../FiltersField/FiltersField";
import { isPackageAnchoredTiming } from "../ScheduleTimingFields/scheduleTimingLabel";

export const useCampaignPopupForm = (
  selectedCell: null | {
    week: string;
    day: WeekdayNumberToString;
  },
  onClose: () => void,
) => {
  const { selectedAutomation } = useAppSelector((store) => store.programAutomation);
  const [loading, setLoading] = useState(false);
  const dispatch = useAppDispatch();
  const { t } = useTranslation(["automation", "common"]);
  const {
    openFormModal,
    toggleModalForm: toggleCampaignPopupForm,
    formModel,
    editedEntity,
    showModalForm: showCampaignPopupForm,
    hideFormModal,
  } = useFormHandler<CampaignMessageComposeFormModel, ScheduledPopUpMessagesAutomationFromAutomation>();

  const handleCampaignSubmit = async (formData: CampaignMessageComposeFormModel) => {
    if (!selectedAutomation) {
      return;
    }

    setLoading(true);

    try {
      // Build the campaign data based on content type
      let campaignData: ScheduledPopUpMessagesAutomationFromAutomation["data"];

      if (formData.contentType === MediaContentType.None) {
        campaignData = {
          contentType: MediaContentType.None,
          visualType: formData.visualType || "sheet",
          message: {
            title: formData.message.title,
            description: formData.message.description,
            ctaLink: formData.message.ctaLink || null,
            ctaText: formData.message.ctaText || null,
            pushTitle: formData.message.pushTitle || null,
            pushDescription: formData.message.pushDescription || null,
          },
        };
      } else if (formData.contentType === MediaContentType.Image) {
        campaignData = {
          contentType: MediaContentType.Image,
          visualType: formData.visualType || "sheet",
          message: {
            title: formData.message.title,
            description: formData.message.description,
            ctaLink: formData.message.ctaLink || null,
            ctaText: formData.message.ctaText || null,
            pushTitle: formData.message.pushTitle || null,
            pushDescription: formData.message.pushDescription || null,
            image: (Array.isArray(formData.message.image) ? formData.message.image[0] : formData.message.image) || "",
          },
        };
      } else if (formData.contentType === MediaContentType.Video) {
        campaignData = {
          contentType: MediaContentType.Video,
          visualType: formData.visualType || "sheet",
          message: {
            title: formData.message.title,
            description: formData.message.description,
            ctaLink: formData.message.ctaLink || null,
            ctaText: formData.message.ctaText || null,
            pushTitle: formData.message.pushTitle || null,
            pushDescription: formData.message.pushDescription || null,
            videoId: formData.message.videoId || "",
            hlsUrl: null,
            mp4Url: null,
            thumbnailUrl: null,
            guid: "",
            previewUrl: null,
          },
        };
      } else if (formData.contentType === MediaContentType.Youtube) {
        campaignData = {
          contentType: MediaContentType.Youtube,
          visualType: formData.visualType || "sheet",
          message: {
            title: formData.message.title,
            description: formData.message.description,
            ctaLink: formData.message.ctaLink || null,
            ctaText: formData.message.ctaText || null,
            pushTitle: formData.message.pushTitle || null,
            pushDescription: formData.message.pushDescription || null,
            videoLink: formData.message.videoLink || "",
          },
        };
      } else {
        throw new Error("Invalid content type");
      }

      const bag = selectedAutomation.packageAnchoredTasks ?? [];

      if (editedEntity) {
        const updated: ProgramAutomationScheduledTask = {
          ...editedEntity,
          data: {
            ...editedEntity.data,
            ...campaignData,
          },
          sentTime: {
            hours: formData.sentTime.get("hour"),
            minutes: formData.sentTime.get("minutes"),
            mode: formData.sentTimeConfig?.mode ?? "absolute",
            avoidSendingOnTheNight: formData.sentTimeConfig?.avoidSendingOnTheNight ?? true,
            calculateFullRangeOverNight: formData.sentTimeConfig?.calculateFullRangeOverNight ?? false,
          },
          name: formData.name,
          templateId: formData.id || null,
          authorId: formData.authorId,
          authorRole: formData.authorRole,
          authorName: formData.authorName,
          authorAvatar: formData.authorAvatar || null,
          filters: transformFromFormDataToFilters(formData.filters),
          filterOperator: formData.filterOperator,
          scheduleTiming: formData.scheduleTiming ?? null,
        };

        const wasInBag = bag.some((task) => task.id === updated.id);
        const isPackageAnchored = isPackageAnchoredTiming(updated.scheduleTiming);
        const automationUpdate: Partial<ProgramAutomation> = {};

        if (isPackageAnchored) {
          automationUpdate.packageAnchoredTasks = wasInBag
            ? bag.map((task) => (task.id === updated.id ? updated : task))
            : [...bag, updated];

          // a grid pop-up edited into a package anchor moves out of its cell
          if (!wasInBag && selectedCell) {
            const weekToUpdate = cloneDeep(selectedAutomation.schedule[selectedCell.week]) || {};
            weekToUpdate[selectedCell.day] = {
              id: weekToUpdate[selectedCell.day]?.id ?? uuid(),
              items: weekToUpdate[selectedCell.day]?.items.filter((item) => item.id !== updated.id) ?? [],
            };
            automationUpdate.schedule = {
              ...selectedAutomation.schedule,
              [selectedCell.week]: weekToUpdate,
            };
          }
        } else {
          // a bag pop-up switched back to the week/day anchor lands in week 1 / day 1
          const cell = selectedCell ?? { week: "1", day: "1" as WeekdayNumberToString };
          const weekToUpdate = cloneDeep(selectedAutomation.schedule[cell.week]) || {};

          if (wasInBag) {
            weekToUpdate[cell.day] = {
              id: weekToUpdate[cell.day]?.id ?? uuid(),
              items: [...(weekToUpdate[cell.day]?.items || []), updated],
            };
            automationUpdate.packageAnchoredTasks = bag.filter((task) => task.id !== updated.id);
          } else {
            weekToUpdate[cell.day] = {
              id: weekToUpdate[cell.day]?.id ?? "",
              items: weekToUpdate[cell.day]?.items.map((item) => (item.id !== updated.id ? item : updated)) ?? [],
            };
          }

          automationUpdate.schedule = {
            ...selectedAutomation.schedule,
            [cell.week]: weekToUpdate,
          };
        }

        await dispatch(
          programAutomationActions.updateAutomationTemplate({
            automationId: selectedAutomation.id,
            automation: automationUpdate,
          }),
        ).unwrap();

        setLoading(false);
        hideFormModal();
        onClose();
        void message.success(t("Zaktualizowano pop-up"));
        return;
      }

      const popupAutomation: ProgramAutomationScheduledTask = {
        id: uuid(),
        type: TemplateType.ScheduledPopUpFromAutomation,
        name: formData.name,
        templateId: formData.id || null,
        comment: "",
        attachments: [],
        data: campaignData,
        authorId: formData.authorId,
        authorRole: formData.authorRole,
        authorName: formData.authorName,
        authorAvatar: formData.authorAvatar || null,
        sentTime: {
          hours: formData.sentTime.get("hour"),
          minutes: formData.sentTime.get("minutes"),
          mode: formData.sentTimeConfig?.mode ?? "absolute",
          avoidSendingOnTheNight: formData.sentTimeConfig?.avoidSendingOnTheNight ?? true,
          calculateFullRangeOverNight: formData.sentTimeConfig?.calculateFullRangeOverNight ?? false,
        },
        scheduleTiming: formData.scheduleTiming ?? null,
      };

      let automationUpdate: Partial<ProgramAutomation>;

      if (isPackageAnchoredTiming(popupAutomation.scheduleTiming)) {
        automationUpdate = { packageAnchoredTasks: [...bag, popupAutomation] };
      } else {
        const cell = selectedCell ?? { week: "1", day: "1" as WeekdayNumberToString };
        const weekToUpdate = cloneDeep(selectedAutomation.schedule[cell.week]) || {};

        weekToUpdate[cell.day] = {
          id: weekToUpdate[cell.day]?.id ?? uuid(),
          items: [...(weekToUpdate[cell.day]?.items || []), popupAutomation],
        };

        automationUpdate = {
          schedule: {
            ...selectedAutomation.schedule,
            [cell.week]: weekToUpdate,
          },
        };
      }

      await dispatch(
        programAutomationActions.updateAutomationTemplate({
          automationId: selectedAutomation.id,
          automation: automationUpdate,
        }),
      ).unwrap();

      setLoading(false);
      hideFormModal();
      onClose();
      void message.success(t("Dodano nowy pop-up"));
    } catch (error) {
      setLoading(false);
      void message.error(t("Błąd podczas dodawania pop-up"));
    }
  };

  const setEditedModelForCampaignPopup = (
    item: ScheduledPopUpMessagesAutomationFromAutomation & Pick<ProgramAutomationScheduledTask, "scheduleTiming">,
  ) => {
    const { data, ...model } = structuredClone({
      ...item,
      sendImmediately: false,
      contentType: item.data.contentType,
      message: item.data.message as CampaignMessageComposeFormModel["message"],
      visualType: item.data.visualType || "sheet",
      type: CampaignType.PopUp,
    });

    if (item.data.contentType === MediaContentType.Image) {
      model.message.image = [item.data.message.image];
    }

    openFormModal(
      {
        ...model,
        filters: transformFromFiltersToFormData(model.filters),
        filterOperator: model.filterOperator,
        sentTime: dayjs().set("hour", item.sentTime.hours).set("minutes", item.sentTime.minutes),
        sentTimeConfig: {
          mode: item.sentTime.mode ?? "absolute",
          avoidSendingOnTheNight: item.sentTime.avoidSendingOnTheNight ?? true,
          calculateFullRangeOverNight: item.sentTime.calculateFullRangeOverNight ?? false,
        },
        scheduleTiming: item.scheduleTiming ?? { anchor: ProgramAutomationScheduleAnchor.WeekDay },
      },
      item,
    );
  };

  return {
    showCampaignPopupForm,
    toggleCampaignPopupForm,
    handleCampaignSubmit,
    loading,
    setEditedModelForCampaignPopup,
    model: formModel,
  };
};
